import { supabase } from '@/integrations/supabase/client';
import { evaluateQuestionnairePolicyRpc } from './questionnairePolicyRpc';

export type CustomQuestionnaireStatus = 'draft' | 'published' | 'archived';
export type CustomQuestionnaireRequestStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';
export type CustomQuestionnaireScope = 'supplier' | 'product' | 'service' | 'location' | 'customer';

export interface CustomQuestionnaireCatalogQuestion {
  canonicalQuestionId: string;
  sourceQuestionId: string;
  questionKey: string;
  prompt: string;
  category: string | null;
  subcategory: string | null;
  questionType: string;
  widgetType: string | null;
  required: boolean;
  requiresLlm: boolean;
  weight: number;
  revisionNumber: number;
  compatibility: 'compatible' | 'breaking';
  reuseScope: CustomQuestionnaireScope;
  retired: boolean;
  sectionTitle: string | null;
  sourceOrder: [number, number];
}

export interface CustomQuestionnaireTemplate {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  questions: CustomQuestionnaireCatalogQuestion[];
}

export interface CustomQuestionnaireSummary {
  id: string;
  status: CustomQuestionnaireStatus;
  lockVersion: number;
  name: string;
  introduction: string | null;
  publishedVersionId: string | null;
  publishedVersionNumber: number | null;
  questionCount: number;
  updatedAt: string;
  archivedAt?: string | null;
  archivedByEntitlement: boolean;
}

export interface CustomQuestionnaireSupplier {
  supplierId: string;
  vendorProfileId: string;
  name: string;
  status: string;
  criticalityTier: string | null;
}

export interface CustomQuestionnaireRequestSummary {
  requestId: string;
  questionnaireId: string;
  questionnaireVersionId: string;
  versionNumber: number;
  questionnaireName: string;
  supplierId: string | null;
  supplierName: string | null;
  vendorProfileId: string;
  status: CustomQuestionnaireRequestStatus;
  scopeType: CustomQuestionnaireScope;
  scopeId: string | null;
  questionCount: number;
  answeredCount: number;
  comparableScore: number | null;
  scoreStatus: 'complete' | 'pending' | 'not_applicable' | 'failed' | null;
  scoreCoveragePercentage: number | null;
  requestedAt: string;
  completedAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
}

export interface CustomQuestionnaireWorkspace {
  canManage: boolean;
  questionnaires: CustomQuestionnaireSummary[];
  archivedQuestionnaires: CustomQuestionnaireSummary[];
  templates: CustomQuestionnaireTemplate[];
  suppliers: CustomQuestionnaireSupplier[];
  requests: CustomQuestionnaireRequestSummary[];
}

export interface CustomQuestionnaireRecipientFilters {
  search?: string;
  tier?: string;
  supplierType?: string;
  status?: string;
  requestStatus?: string;
  departmentId?: string | null;
}

export interface CustomQuestionnaireRecipient {
  supplierId: string;
  vendorProfileId: string;
  name: string;
  domain: string | null;
  customerVendorCode: string | null;
  supplierType: string | null;
  criticalityTier: string | null;
  status: string;
  latestRequestStatus: CustomQuestionnaireRequestStatus | null;
  latestRequestedAt: string | null;
  eligible: boolean;
  ineligibleReason: 'active_request_exists' | null;
}

export interface CustomQuestionnaireRecipientPage {
  rows: CustomQuestionnaireRecipient[];
  page: number;
  pageSize: number;
  total: number;
  eligibleTotal: number;
  facets: {
    tiers: string[];
    supplierTypes: string[];
    statuses: string[];
  };
}

export type CustomQuestionnaireRecipientSelection =
  | {
      mode: 'explicit';
      vendorProfileIds: string[];
      excludedVendorProfileIds?: never;
      filters?: CustomQuestionnaireRecipientFilters;
    }
  | {
      mode: 'all_matching';
      vendorProfileIds?: never;
      excludedVendorProfileIds: string[];
      filters: CustomQuestionnaireRecipientFilters;
    };

export interface CustomQuestionnaireBatchPreview {
  selectedCount: number;
  eligibleCount: number;
  skippedCount: number;
  skippedReasons: Record<string, number>;
}

export interface CustomQuestionnaireRequestBatch {
  batchId: string;
  questionnaireId: string;
  questionnaireVersionId: string;
  questionnaireName: string;
  versionNumber: number;
  status: 'queued' | 'processing' | 'completed' | 'completed_with_errors' | 'cancelled';
  selectedCount: number;
  queuedCount: number;
  createdCount: number;
  skippedCount: number;
  failedCount: number;
  completedRequestCount: number;
  openRequestCount: number;
  requestedAt: string;
  completedAt: string | null;
}

export interface CustomQuestionnaireRequestBatchPage {
  rows: CustomQuestionnaireRequestBatch[];
  page: number;
  pageSize: number;
  total: number;
}

export interface CustomQuestionnaireDraft {
  id: string;
  status: CustomQuestionnaireStatus;
  lockVersion: number;
  name: string;
  introduction: string | null;
  publishedVersionId: string | null;
  items: Array<{
    id: string;
    canonicalQuestionId: string;
    displayOrder: number;
  }>;
}

export interface CustomQuestionnaireQuestionSnapshot {
  policyRevision?: number;
  scoringRole?: 'scored' | 'context';
  questionId: string;
  canonicalQuestionId: string;
  revisionNumber: number;
  revisionCompatibility: 'compatible' | 'breaking';
  questionKey: string;
  prompt: string;
  category: string | null;
  subcategory: string | null;
  questionType: string;
  widgetType: string | null;
  cardinality: string | null;
  scoringMode: string | null;
  weight: number;
  required: boolean;
  requiresLlm: boolean;
  options: Array<{
    id: string;
    code: string;
    label: string;
    order: number;
    score: number | null;
    riskLevel: string | null;
    criticalTrigger: boolean;
    semanticKind?: 'standard' | 'none' | 'other';
    exclusive?: boolean;
    requiresExplanation?: boolean;
    scoreEffect?: 'rubric' | 'fixed' | 'unscored';
  }>;
  // Earlier dispatchers stored null when an optional knowledge-base row was absent.
  // Keep frozen requests readable; new dispatches always carry an object.
  knowledgeBase: Record<string, unknown> | null;
  rules: Record<string, unknown>;
}

export interface CustomQuestionnairePreview {
  questionnaireId: string;
  definition: {
    name: string;
    introduction: string | null;
    versionNumber: number;
    publishedAt: string;
  };
  items: Array<{
    id: string;
    displayOrder: number;
    question: CustomQuestionnaireQuestionSnapshot;
  }>;
}

export interface CustomQuestionnaireRequestDetail {
  requestId: string;
  organizationId: string;
  vendorProfileId: string;
  status: CustomQuestionnaireRequestStatus;
  scopeType: CustomQuestionnaireScope;
  scopeId: string | null;
  definition: {
    name: string;
    introduction: string | null;
    versionNumber: number;
    sentAt: string;
  };
  items: Array<{
    id: string;
    applicable?: boolean;
    scored?: boolean;
    displayOrder: number;
    question: CustomQuestionnaireQuestionSnapshot;
    responsePayload: Record<string, unknown> | null;
    responseNotes: string | null;
    evidence: Array<Record<string, unknown>>;
    reuseStatus: string;
    reused: boolean | null;
    editable: boolean;
  }>;
  response: {
    id: string;
    autoCompleted: boolean;
    comparableScore: number | null;
    scoreStatus: 'complete' | 'pending' | 'not_applicable' | 'failed';
    scoringError?: string | null;
    scoreCoveragePercentage: number;
    grade?: string | null;
    questionScores?: Array<Record<string, unknown>> | null;
    scoringContext: Record<string, unknown>;
    completedAt: string;
  } | null;
}

export interface SupplierCustomQuestionnaireQueueItem {
  requestId: string;
  questionnaireName: string;
  introduction: string | null;
  versionNumber: number;
  customerName: string;
  customerLogoUrl: string | null;
  status: CustomQuestionnaireRequestStatus;
  scopeType: CustomQuestionnaireScope;
  scopeId: string | null;
  questionCount: number;
  answeredCount: number;
  reusedCount: number;
  comparableScore: number | null;
  scoreStatus: 'complete' | 'pending' | 'not_applicable' | 'failed' | null;
  requestedAt: string;
  completedAt: string | null;
  cancelledAt: string | null;
}

type RpcResponse<T> = Promise<{ data: T | null; error: { message?: string; details?: string | null } | null }>;

function rpc<T>(name: string, args: Record<string, unknown>): RpcResponse<T> {
  return supabase.rpc(name as never, args as never) as unknown as RpcResponse<T>;
}

export function customQuestionnaireErrorMessage(error: { message?: string; details?: string | null } | null) {
  const raw = `${error?.message ?? ''} ${error?.details ?? ''}`;
  if (raw.includes('custom_questionnaire_not_entitled')) return 'Custom questionnaires are not enabled for this account.';
  if (raw.includes('custom_questionnaire_forbidden')) return 'You do not have permission to manage custom questionnaires.';
  if (raw.includes('custom_questionnaire_version_conflict')) return 'This questionnaire changed in another session. Reload it before saving again.';
  if (raw.includes('custom_questionnaire_history_')) return 'Published and completed questionnaire records cannot be changed. Create a new questionnaire version instead.';
  if (raw.includes('custom_questionnaire_source_retired')) return 'A selected platform question has been retired. Remove it before publishing or sending.';
  if (raw.includes('custom_questionnaire_request_cancelled')) return 'This request was cancelled when the account entitlement was removed.';
  if (raw.includes('custom_questionnaire_supplier_not_found')) return 'That supplier is no longer available to this account.';
  if (raw.includes('required_answers_missing')) return 'Answer every required question before submitting.';
  if (raw.includes('invalid_evidence')) return 'Evidence links must use HTTPS and meet the platform limits.';
  if (raw.includes('invalid_option_code')) return 'One of the selected answers is no longer valid for this questionnaire version.';
  if (raw.includes('custom_questionnaire_validation_failed')) return 'Check the questionnaire details and try again.';
  return error?.message || 'The custom questionnaire operation failed.';
}

async function requireData<T>(result: Awaited<RpcResponse<T>>, fallback: T): Promise<T> {
  if (result.error) throw new Error(customQuestionnaireErrorMessage(result.error));
  return result.data ?? fallback;
}

export async function fetchCustomQuestionnaireWorkspace(organizationId: string) {
  return requireData(
    await rpc<CustomQuestionnaireWorkspace>('get_custom_questionnaire_workspace', {
      p_organization_id: organizationId,
    }),
    { canManage: false, questionnaires: [], archivedQuestionnaires: [], templates: [], suppliers: [], requests: [] },
  );
}

export async function fetchCustomQuestionnaireRecipients(params: {
  organizationId: string;
  questionnaireId: string;
  filters?: CustomQuestionnaireRecipientFilters;
  page?: number;
  pageSize?: number;
}) {
  return requireData(
    await rpc<CustomQuestionnaireRecipientPage>('get_custom_questionnaire_recipients', {
      p_organization_id: params.organizationId,
      p_questionnaire_id: params.questionnaireId,
      p_filters: params.filters ?? {},
      p_page: params.page ?? 1,
      p_page_size: params.pageSize ?? 50,
    }),
    { rows: [], page: 1, pageSize: params.pageSize ?? 50, total: 0, eligibleTotal: 0, facets: { tiers: [], supplierTypes: [], statuses: [] } },
  );
}

export async function previewCustomQuestionnaireRequestBatch(params: {
  organizationId: string;
  questionnaireId: string;
  selection: CustomQuestionnaireRecipientSelection;
}) {
  return requireData(
    await rpc<CustomQuestionnaireBatchPreview>('preview_custom_questionnaire_request_batch', {
      p_organization_id: params.organizationId,
      p_questionnaire_id: params.questionnaireId,
      p_selection: params.selection,
    }),
    null as never,
  );
}

export async function createCustomQuestionnaireRequestBatch(params: {
  organizationId: string;
  questionnaireId: string;
  selection: CustomQuestionnaireRecipientSelection;
  idempotencyKey: string;
}) {
  return requireData(
    await rpc<{
      batchId: string;
      status: CustomQuestionnaireRequestBatch['status'];
      selectedCount: number;
      queuedCount: number;
      createdCount: number;
      skippedCount: number;
      failedCount: number;
      replayed: boolean;
    }>('create_custom_questionnaire_request_batch', {
      p_organization_id: params.organizationId,
      p_questionnaire_id: params.questionnaireId,
      p_selection: params.selection,
      p_idempotency_key: params.idempotencyKey,
    }),
    null as never,
  );
}

export async function fetchCustomQuestionnaireRequestBatches(organizationId: string, page = 1, pageSize = 25) {
  return requireData(
    await rpc<CustomQuestionnaireRequestBatchPage>('get_custom_questionnaire_request_batches', {
      p_organization_id: organizationId,
      p_page: page,
      p_page_size: pageSize,
    }),
    { rows: [], page, pageSize, total: 0 },
  );
}

export async function fetchCustomQuestionnaireEditor(organizationId: string, questionnaireId: string) {
  return requireData(
    await rpc<CustomQuestionnaireDraft>('get_custom_questionnaire_editor', {
      p_organization_id: organizationId,
      p_questionnaire_id: questionnaireId,
    }),
    null as never,
  );
}

export async function fetchCustomQuestionnairePreview(organizationId: string, questionnaireId: string) {
  return requireData(
    await rpc<CustomQuestionnairePreview>('get_custom_questionnaire_preview', {
      p_organization_id: organizationId,
      p_questionnaire_id: questionnaireId,
    }),
    null as never,
  );
}

type DraftPayload = {
  name: string;
  introduction?: string | null;
  canonicalQuestionIds: string[];
};

function commandPayload(payload: DraftPayload) {
  return {
    name: payload.name,
    introduction: payload.introduction ?? null,
    items: payload.canonicalQuestionIds.map((canonicalQuestionId) => ({ canonicalQuestionId })),
  };
}

async function runCommand(params: {
  organizationId: string;
  action: 'create' | 'save_draft' | 'publish' | 'archive' | 'restore';
  questionnaireId?: string | null;
  expectedLockVersion?: number | null;
  payload?: Record<string, unknown>;
}) {
  return requireData(
    await rpc<{ id: string; versionId: string | null; versionNumber: number | null; lockVersion: number; status: CustomQuestionnaireStatus }>(
      'custom_questionnaire_command',
      {
        p_organization_id: params.organizationId,
        p_action: params.action,
        p_questionnaire_id: params.questionnaireId ?? null,
        p_expected_lock_version: params.expectedLockVersion ?? null,
        p_payload: params.payload ?? {},
      },
    ),
    null as never,
  );
}

export function createCustomQuestionnaire(organizationId: string, payload: DraftPayload) {
  return runCommand({ organizationId, action: 'create', payload: commandPayload(payload) });
}

export function saveCustomQuestionnaireDraft(
  organizationId: string,
  questionnaireId: string,
  expectedLockVersion: number,
  payload: DraftPayload,
) {
  return runCommand({
    organizationId,
    action: 'save_draft',
    questionnaireId,
    expectedLockVersion,
    payload: commandPayload(payload),
  });
}

export function publishCustomQuestionnaire(organizationId: string, questionnaireId: string, expectedLockVersion: number) {
  return runCommand({ organizationId, action: 'publish', questionnaireId, expectedLockVersion });
}

export function archiveCustomQuestionnaire(organizationId: string, questionnaireId: string, expectedLockVersion: number) {
  return runCommand({
    organizationId,
    action: 'archive',
    questionnaireId,
    expectedLockVersion,
    payload: { reason: 'Archived by customer administrator' },
  });
}

export function restoreCustomQuestionnaire(organizationId: string, questionnaireId: string, expectedLockVersion: number) {
  return runCommand({ organizationId, action: 'restore', questionnaireId, expectedLockVersion });
}

export async function sendCustomQuestionnaire(params: {
  organizationId: string;
  questionnaireId: string;
  vendorProfileId: string;
  idempotencyKey: string;
}) {
  return requireData(
    await rpc<{ requestId: string; status: CustomQuestionnaireRequestStatus; autoCompleted: boolean; responseId?: string | null; replayed: boolean }>(
      'send_custom_questionnaire',
      {
        p_organization_id: params.organizationId,
        p_questionnaire_id: params.questionnaireId,
        p_vendor_profile_id: params.vendorProfileId,
        p_idempotency_key: params.idempotencyKey,
      },
    ),
    null as never,
  );
}

export async function fetchCustomQuestionnaireRequest(requestId: string) {
  return requireData(
    await rpc<CustomQuestionnaireRequestDetail>('get_custom_questionnaire_request', { p_request_id: requestId }),
    null as never,
  );
}

export async function evaluateCustomQuestionnairePolicy(requestId: string, answers: Array<{
  itemId: string; responsePayload: Record<string, unknown>; notes?: string | null; evidence?: unknown;
}>) {
  return evaluateQuestionnairePolicyRpc('evaluate_custom_questionnaire_policy', () =>
    rpc<unknown>('evaluate_custom_questionnaire_policy', { p_request_id: requestId, p_answers: answers }), answers.map((answer) => ({ questionId: answer.itemId, questionKey: answer.itemId })));
}

export async function fetchSupplierCustomQuestionnaireQueue(vendorProfileId: string) {
  return requireData(
    await rpc<SupplierCustomQuestionnaireQueueItem[]>('get_supplier_custom_questionnaire_queue', {
      p_vendor_profile_id: vendorProfileId,
    }),
    [],
  );
}

export const getSupplierCustomQuestionnaireOpenCountQueryKey = (vendorProfileId?: string | null) => [
  'supplier-custom-questionnaire-open-count',
  vendorProfileId ?? null,
] as const;

export async function fetchSupplierCustomQuestionnaireOpenCount(vendorProfileId: string) {
  return requireData(
    await rpc<number>('get_supplier_custom_questionnaire_open_count', {
      p_vendor_profile_id: vendorProfileId,
    }),
    0,
  );
}

export async function submitCustomQuestionnaireResponse(
  requestId: string,
  answers: Array<{
    itemId: string;
    responsePayload: Record<string, unknown>;
    notes?: string | null;
    evidence?: Array<Record<string, unknown>>;
  }>,
) {
  return requireData(
    await rpc<{ requestId: string; responseId: string; status: 'completed'; replayed: boolean }>(
      'submit_custom_questionnaire_response',
      { p_request_id: requestId, p_answers: answers },
    ),
    null as never,
  );
}

export async function scoreCustomQuestionnaireIfPending(requestId: string) {
  const detail = await fetchCustomQuestionnaireRequest(requestId);
  if (detail.response?.scoreStatus !== 'pending') return detail.response;

  const { error } = await supabase.functions.invoke('score-questionnaire', {
    body: { custom_request_id: requestId, organizationId: detail.organizationId },
  });
  if (error) throw new Error(error.message || 'Custom questionnaire scoring remains queued.');
  return (await fetchCustomQuestionnaireRequest(requestId)).response;
}
